/**
 * PRODUCT REGISTRY — single source of truth for the products the submittal
 * builder supports.
 *
 * Adding a new product should be a matter of adding an entry here (plus its
 * worker content and document set), rather than hunting down hardcoded
 * `if (isStructuralFloor)` branches across the codebase.
 *
 * NOTE: This commit introduces the registry and migrates the two EXISTING
 * products onto it with no behavior change. New products are added in a
 * later commit.
 */

// The canonical ProductType is derived from the registry keys below.
export type ProductType = 'structural-floor' | 'underlayment' | 'structural-wall';

export interface ProductConfig {
  /** Stable identifier used in data, URLs, and the productType field. */
  id: ProductType;
  /** Short label shown on the product-selection card. */
  name: string;
  /** One-line description shown on the product-selection card. */
  description: string;
  /** Available panel thicknesses, in display order. */
  sizes: string[];
  /** Default selected size (must be one of `sizes`). */
  defaultSize: string;
  /** Helper text shown beneath the size dropdown on the project form. */
  sizeHelpText: string;
  /** CSI section code printed on the cover page (e.g. "SECTION 06 16 23"). */
  sectionCode: string;
  /**
   * Exact value sent to the HubSpot "product type" dropdown (its INTERNAL VALUE,
   * not the display `name`). Must match the option configured in HubSpot, e.g.
   * "Structural Floor" / "Underlayment" / "Structural Wall" — note: no "Panel".
   */
  hubspotValue: string;
}

export const PRODUCTS: Record<ProductType, ProductConfig> = {
  'structural-floor': {
    id: 'structural-floor',
    name: 'Structural Floor Panel',
    description: 'MAXTERRA® MgO Non-Combustible Structural Floor Panels',
    sizes: ['3/4-in (20mm)'],
    defaultSize: '3/4-in (20mm)',
    sizeHelpText: 'Structural Floor Panels are available in 3/4-in thickness',
    sectionCode: 'SECTION 06 16 23',
    hubspotValue: 'Structural Floor',
  },
  underlayment: {
    id: 'underlayment',
    name: 'Underlayment Panel',
    description: 'MAXTERRA® MgO Fire- And Water-Resistant Underlayment Panels',
    sizes: ['1/2-in (12mm)', '5/8-in (16mm)'],
    defaultSize: '1/2-in (12mm)',
    sizeHelpText: 'Underlayment Panels are available in 1/2-in and 5/8-in thickness',
    sectionCode: 'SECTION 06 16 26',
    hubspotValue: 'Underlayment',
  },
  'structural-wall': {
    id: 'structural-wall',
    name: 'Structural Wall Panel',
    description: 'MAXTERRA® MgO Non-Combustible Structural Wall Panels',
    sizes: ['1/2-in (12mm)', '5/8-in (16mm)'],
    defaultSize: '1/2-in (12mm)',
    sizeHelpText: 'Structural Wall Panels are available in 1/2-in and 5/8-in thickness',
    sectionCode: 'SECTION 06 16 00',
    hubspotValue: 'Structural Wall',
  },
};

/** Products in display order, for rendering the selection cards. */
export const PRODUCT_LIST: ProductConfig[] = Object.values(PRODUCTS);

/** All valid product ids. */
export const PRODUCT_TYPES: ProductType[] = Object.keys(PRODUCTS) as ProductType[];

/** The default product used when none has been selected yet. */
export const DEFAULT_PRODUCT_TYPE: ProductType = 'structural-floor';

/** Type guard: is the given string a known product id? */
export function isProductType(value: unknown): value is ProductType {
  return typeof value === 'string' && value in PRODUCTS;
}

/**
 * Look up a product config by id. Falls back to the default product so callers
 * always receive a valid config (matching the pre-registry behavior where an
 * unknown/empty productType defaulted to structural-floor).
 */
export function getProduct(id?: string | null): ProductConfig {
  if (isProductType(id)) {
    return PRODUCTS[id];
  }
  return PRODUCTS[DEFAULT_PRODUCT_TYPE];
}
